import { useEffect } from 'react';

const SCROLLABLE_OVERFLOW = /(auto|scroll|overlay)/;

/**
 * Scroll containers between `element` and `document.body`, plus AG Grid viewport when present.
 */
function collectScrollContainers(element) {
    const containers = new Set();
    let node = element?.parentElement;

    while (node && node !== document.body) {
        const { overflowY, overflowX } = window.getComputedStyle(node);
        if (SCROLLABLE_OVERFLOW.test(overflowY) || SCROLLABLE_OVERFLOW.test(overflowX)) {
            containers.add(node);
        }
        node = node.parentElement;
    }

    ['.ag-body-viewport', '.ag-center-cols-viewport'].forEach((selector) => {
        const viewport = element?.closest?.(selector);
        if (viewport) {
            containers.add(viewport);
        }
    });

    return [...containers];
}

/**
 * Invokes `onClose` when any scrollable ancestor of `anchorRef` scrolls.
 * Use with portaled popovers/menus so they do not drift while the row scrolls away.
 */
export function useCloseOnAncestorScroll({ enabled, anchorRef, onClose }) {
    useEffect(() => {
        if (!enabled || !onClose) {
            return undefined;
        }

        const anchor = anchorRef?.current;
        if (!anchor) {
            return undefined;
        }

        const scrollContainers = collectScrollContainers(anchor);
        if (scrollContainers.length === 0) {
            return undefined;
        }

        const handleScroll = () => {
            onClose();
        };

        scrollContainers.forEach((el) => {
            el.addEventListener('scroll', handleScroll, { passive: true });
        });

        return () => {
            scrollContainers.forEach((el) => {
                el.removeEventListener('scroll', handleScroll);
            });
        };
    }, [enabled, anchorRef, onClose]);
}
